import {useMemo, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {IApplicationRootState} from "../../../applicationState";
import {DisplayProductSummary, FacetedFilters, IResearchPageState, IResearchPagination} from "../../model/ResearchPageState.model";
import researchPageActions from "./ResearchPageActions";
import {Ticker} from "../../model/product/Ticker";
import {AnyThunkDispatch} from "../../../commonTypes";

export const FILTER_FIELDS = [
    "assetType",
    "productRating",
    "oddRatingCurrent",
    "approach",
    "category",
    "region",
    "style",
] as const;

const SEARCH_FIELDS: (keyof DisplayProductSummary)[] = [
    "managerName",
    "name",
    "assetType",
    "productRating",
    "oddRatingCurrent",
    "approach",
    "category",
    "region",
    "style",
];

export interface IFilterOption {
    value: string;
    count: number;
}

export interface IFacetedCounts {
    [field: string]: IFilterOption[];
}

export interface IFilterActionCreators {
    updateFacetedFiltersAction: (filters: FacetedFilters) => any;
    updateSearchTextAction: (text: string) => any;
    updatePaginationAction: (pagination: IResearchPagination) => any;
    clearAllFiltersAction: () => any;
}

export interface IResearchFilterConfig {
    stateSelector: (state: IApplicationRootState) => IResearchPageState | undefined;
    actions: IFilterActionCreators;
    filterFields: readonly string[];
    searchFields: readonly (keyof DisplayProductSummary)[];
}

export interface IUseResearchFiltersResult {
    filteredData: DisplayProductSummary[];
    paginatedData: DisplayProductSummary[];
    facetedCounts: IFacetedCounts;
    facetedFilters: FacetedFilters;
    searchText: string;
    pagination: IResearchPagination;
    totalFiltered: number;
    hasActiveFilters: boolean;
    activeFilterCount: number;
    filterFields: readonly string[];
    setFilter: (field: string, values: Set<string>) => void;
    toggleFilterValue: (field: string, value: string) => void;
    setSearchText: (text: string) => void;
    setPagination: (pagination: IResearchPagination) => void;
    clearAllFilters: () => void;
    removeFilterValue: (field: string, value: string) => void;
}

function getFieldValue(row: DisplayProductSummary, field: string): string {
    return (row[field as keyof DisplayProductSummary] as string ?? "").toString();
}

function rowMatchesSearch(
    row: DisplayProductSummary,
    searchLower: string,
    searchFields: readonly (keyof DisplayProductSummary)[],
): boolean {
    if (!searchLower) return true;

    return searchFields.some((field) => getFieldValue(row, field).toLowerCase().includes(searchLower))
        || row.tickers?.some((t: Ticker) => t.name.toLowerCase().includes(searchLower))
        || false;
}

function rowMatchesFilters(row: DisplayProductSummary, filters: FacetedFilters): boolean {
    for (const field of Object.keys(filters)) {
        const activeValues = filters[field];
        if (!activeValues || activeValues.size === 0) continue;
        const rowValue = getFieldValue(row, field);
        if (!activeValues.has(rowValue)) return false;
    }
    return true;
}

const defaultStateSelector = (s: IApplicationRootState) => s.researchPageState;

export function useResearchFilters(allData: DisplayProductSummary[], config?: IResearchFilterConfig): IUseResearchFiltersResult {
    const dispatch: AnyThunkDispatch = useDispatch();

    const stateSelector = config?.stateSelector ?? defaultStateSelector;
    const actions = config?.actions ?? researchPageActions;
    const filterFields = config?.filterFields ?? FILTER_FIELDS;
    const searchFields = config?.searchFields ?? SEARCH_FIELDS;

    const state: IResearchPageState | undefined = useSelector(stateSelector);

    const facetedFilters = state?.facetedFilters ?? {};
    const searchText = state?.searchText ?? "";
    const pagination = state?.pagination ?? {pageIndex: 0, pageSize: 50};

    const searchLower = searchText.trim().toLowerCase();

    const filteredData = useMemo(() => {
        return allData.filter((row) =>
            rowMatchesSearch(row, searchLower, searchFields)
            && rowMatchesFilters(row, facetedFilters));
    }, [allData, searchLower, facetedFilters, searchFields]);

    const facetedCounts: IFacetedCounts = useMemo(() => {
        const counts: IFacetedCounts = {};
        const fieldCountMaps: Record<string, Record<string, number>> = {};
        for (const field of filterFields) {
            fieldCountMaps[field] = {};
        }

        for (const row of allData) {
            if (!rowMatchesSearch(row, searchLower, searchFields)) continue;

            for (const field of filterFields) {
                const otherFieldsMatch = Object.keys(facetedFilters).every((f) => {
                    if (f === field) return true;
                    const activeValues = facetedFilters[f];
                    if (!activeValues || activeValues.size === 0) return true;
                    return activeValues.has(getFieldValue(row, f));
                });

                if (otherFieldsMatch) {
                    const val = getFieldValue(row, field);
                    fieldCountMaps[field][val] = (fieldCountMaps[field][val] || 0) + 1;
                }
            }
        }

        for (const field of filterFields) {
            counts[field] = Object.entries(fieldCountMaps[field])
                .map(([value, count]) => ({value, count}))
                .sort((a, b) => a.value.localeCompare(b.value));
        }
        return counts;
    }, [allData, facetedFilters, searchLower, filterFields, searchFields]);

    const totalFiltered = filteredData.length;

    const paginatedData = useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize;
        return filteredData.slice(start, start + pagination.pageSize);
    }, [filteredData, pagination]);

    const hasActiveFilters = useMemo(() =>
        searchText.length > 0 || Object.values(facetedFilters).some((s) => s.size > 0),
    [searchText, facetedFilters]);

    const activeFilterCount = useMemo(() =>
        Object.values(facetedFilters).reduce((sum, s) => sum + s.size, 0),
    [facetedFilters]);

    const setFilter = useCallback((field: string, values: Set<string>) => {
        const next = {...facetedFilters, [field]: values};
        dispatch(actions.updateFacetedFiltersAction(next));
    }, [dispatch, facetedFilters, actions]);

    const toggleFilterValue = useCallback((field: string, value: string) => {
        const current = facetedFilters[field] ?? new Set<string>();
        const next = new Set(current);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        const updated = {...facetedFilters, [field]: next};
        dispatch(actions.updateFacetedFiltersAction(updated));
    }, [dispatch, facetedFilters, actions]);

    const removeFilterValue = useCallback((field: string, value: string) => {
        const current = facetedFilters[field];
        if (!current) return;
        const next = new Set(current);
        next.delete(value);
        const updated = {...facetedFilters, [field]: next};
        dispatch(actions.updateFacetedFiltersAction(updated));
    }, [dispatch, facetedFilters, actions]);

    const setSearchText = useCallback((text: string) => {
        dispatch(actions.updateSearchTextAction(text));
    }, [dispatch, actions]);

    const setPagination = useCallback((p: IResearchPagination) => {
        dispatch(actions.updatePaginationAction(p));
    }, [dispatch, actions]);

    const clearAllFilters = useCallback(() => {
        dispatch(actions.clearAllFiltersAction());
    }, [dispatch, actions]);

    return {
        filteredData,
        paginatedData,
        facetedCounts,
        facetedFilters,
        searchText,
        pagination,
        totalFiltered,
        hasActiveFilters,
        activeFilterCount,
        filterFields,
        setFilter,
        toggleFilterValue,
        setSearchText,
        setPagination,
        clearAllFilters,
        removeFilterValue,
    };
}
